/**
 * DataLineageEditor — V7-5-3.
 *
 * Interactive graph editor for data lineage (dataset -> transform -> model).
 * Renders an inline SVG with nodes, directed edges, pan/zoom, and a form
 * panel for adding/editing/deleting nodes and edges.
 *
 * Data model types come from packages/core data-lineage-service.
 * API: GET/POST /api/w/:slug/systems/:systemId/lineage
 */

import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Button, Card, Field, Input, Select, EmptyState, Alert, Spinner } from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { req, ApiError } from '../lib/api';

// ============================================================
// Types (aligned with core LineageNode / LineageGraph)
// ============================================================

export type NodeType = 'dataset' | 'transform' | 'model';

export interface LineageNode {
  id: string;
  name: string;
  type: NodeType;
  pii_categories: string[];
  retention_days: number | null;
}

export interface LineageEdge {
  from: string;
  to: string;
}

export interface LineageGraph {
  nodes: LineageNode[];
  edges: LineageEdge[];
}

interface LineageDocument {
  graph: LineageGraph;
  revision: number;
  updated_at: string | null;
}

export interface DataLineageEditorProps {
  systemId: string;
  workspaceSlug: string;
  readOnly?: boolean;
}

// ============================================================
// API helpers
// ============================================================

async function fetchLineage(slug: string, systemId: string): Promise<LineageDocument> {
  try {
    return await req<LineageDocument>(
      `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/lineage`
    );
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return { graph: { nodes: [], edges: [] }, revision: 0, updated_at: null };
    }
    throw e;
  }
}

async function saveLineage(
  slug: string,
  systemId: string,
  graph: LineageGraph,
  revision: number,
): Promise<LineageDocument> {
  return req<LineageDocument>(
    `/api/w/${encodeURIComponent(slug)}/systems/${encodeURIComponent(systemId)}/lineage`,
    {
      method: 'POST',
      body: JSON.stringify({ graph, expected_revision: revision }),
    },
  );
}

// ============================================================
// Layout: simple topological sort for Y, spread for X
// ============================================================

interface LayoutNode {
  id: string;
  x: number;
  y: number;
}

function layoutGraph(nodes: LineageNode[], edges: LineageEdge[]): Map<string, LayoutNode> {
  if (nodes.length === 0) return new Map();

  // Build adjacency and in-degree
  const adj = new Map<string, string[]>();
  const inDeg = new Map<string, number>();
  for (const n of nodes) {
    adj.set(n.id, []);
    inDeg.set(n.id, 0);
  }
  for (const e of edges) {
    adj.get(e.from)?.push(e.to);
    inDeg.set(e.to, (inDeg.get(e.to) ?? 0) + 1);
  }

  // Topological sort (Kahn's) to assign layers
  const layers: string[][] = [];
  let frontier = nodes.filter((n) => (inDeg.get(n.id) ?? 0) === 0).map((n) => n.id);
  const visited = new Set<string>();
  while (frontier.length > 0) {
    layers.push([...frontier]);
    frontier.forEach((id) => visited.add(id));
    const next: string[] = [];
    for (const id of frontier) {
      for (const to of adj.get(id) ?? []) {
        inDeg.set(to, (inDeg.get(to) ?? 0) - 1);
        if (inDeg.get(to) === 0 && !visited.has(to)) next.push(to);
      }
    }
    frontier = next;
  }
  // Orphaned nodes (cycles — shouldn't happen with validated DAG)
  for (const n of nodes) {
    if (!visited.has(n.id)) {
      layers.push([n.id]);
    }
  }

  const NODE_GAP_X = 180;
  const NODE_GAP_Y = 120;
  const result = new Map<string, LayoutNode>();
  for (let ly = 0; ly < layers.length; ly++) {
    const layer = layers[ly];
    const totalWidth = (layer.length - 1) * NODE_GAP_X;
    for (let i = 0; i < layer.length; i++) {
      result.set(layer[i], {
        id: layer[i],
        x: -totalWidth / 2 + i * NODE_GAP_X,
        y: ly * NODE_GAP_Y,
      });
    }
  }
  return result;
}

// ============================================================
// Node type SVG icons (inline, design-token colors)
// ============================================================

const NODE_RADIUS = 28;

function NodeIcon({ type, x, y }: { type: NodeType; x: number; y: number }) {
  switch (type) {
    case 'dataset':
      // Cylinder
      return (
        <g>
          <ellipse cx={x} cy={y - 8} rx={18} ry={8} fill="none" stroke="var(--accent)" strokeWidth="1.5" />
          <rect x={x - 18} y={y - 8} width={36} height={20} fill="var(--surface)" stroke="none" />
          <line x1={x - 18} y1={y - 8} x2={x - 18} y2={y + 12} stroke="var(--accent)" strokeWidth="1.5" />
          <line x1={x + 18} y1={y - 8} x2={x + 18} y2={y + 12} stroke="var(--accent)" strokeWidth="1.5" />
          <ellipse cx={x} cy={y + 12} rx={18} ry={8} fill="none" stroke="var(--accent)" strokeWidth="1.5" />
        </g>
      );
    case 'model':
      // Circle
      return (
        <circle cx={x} cy={y} r={18} fill="none" stroke="var(--success)" strokeWidth="1.5" />
      );
    case 'transform':
      // Gear (simplified hexagon)
      return (
        <polygon
          points={[0, 1, 2, 3, 4, 5]
            .map((i) => {
              const a = (Math.PI / 3) * i - Math.PI / 6;
              return `${x + 18 * Math.cos(a)},${y + 18 * Math.sin(a)}`;
            })
            .join(' ')}
          fill="none"
          stroke="var(--text-muted)"
          strokeWidth="1.5"
        />
      );
    default:
      return <circle cx={x} cy={y} r={18} fill="none" stroke="var(--border)" strokeWidth="1.5" />;
  }
}

// ============================================================
// Arrow marker
// ============================================================

function ArrowMarker() {
  return (
    <defs>
      <marker
        id="arrow"
        viewBox="0 0 10 7"
        refX="10"
        refY="3.5"
        markerWidth="8"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path d="M0 0 L10 3.5 L0 7 Z" fill="var(--text-dim)" />
      </marker>
    </defs>
  );
}

// ============================================================
// Edge line with arrow
// ============================================================

function EdgeLine({
  from,
  to,
  layout,
}: {
  from: string;
  to: string;
  layout: Map<string, LayoutNode>;
}) {
  const a = layout.get(from);
  const b = layout.get(to);
  if (!a || !b) return null;

  // Shorten line so arrow doesn't overlap node
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return null;
  const ux = dx / len;
  const uy = dy / len;
  const r = NODE_RADIUS;

  return (
    <line
      x1={a.x + ux * r}
      y1={a.y + uy * r}
      x2={b.x - ux * r}
      y2={b.y - uy * r}
      stroke="var(--text-dim)"
      strokeWidth="1.5"
      markerEnd="url(#arrow)"
    />
  );
}

// ============================================================
// PII badge
// ============================================================

function PiiBadge({ count, x, y }: { count: number; x: number; y: number }) {
  if (count === 0) return null;
  return (
    <g>
      <circle cx={x + 20} cy={y - 20} r={10} fill="var(--danger-fg)" />
      <text
        x={x + 20}
        y={y - 16}
        textAnchor="middle"
        fontSize="10"
        fontWeight="700"
        fill="var(--text-inverse)"
      >
        {count}
      </text>
    </g>
  );
}

// ============================================================
// Add node form
// ============================================================

interface NodeFormData {
  id: string;
  name: string;
  type: NodeType;
  pii_categories: string;
  retention_days: string;
}

const EMPTY_FORM: NodeFormData = {
  id: '',
  name: '',
  type: 'dataset',
  pii_categories: '',
  retention_days: '',
};

function AddNodeForm({
  onAdd,
  onCancel,
  existingIds,
}: {
  onAdd: (node: LineageNode) => void;
  onCancel: () => void;
  existingIds: Set<string>;
}) {
  const [form, setForm] = useState<NodeFormData>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const id = form.id.trim().replace(/\s+/g, '_');
    if (!id || !/^[a-zA-Z0-9_-]{1,64}$/.test(id)) {
      setError('ID must be 1-64 alphanumeric characters, hyphens, or underscores.');
      return;
    }
    if (existingIds.has(id)) {
      setError('A node with this ID already exists.');
      return;
    }
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }

    const pii = form.pii_categories
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const retention = form.retention_days.trim()
      ? parseInt(form.retention_days, 10)
      : null;
    if (retention !== null && (isNaN(retention) || retention < 0 || retention > 36500)) {
      setError('Retention must be 0-36500 days or blank.');
      return;
    }

    onAdd({
      id,
      name: form.name.trim(),
      type: form.type,
      pii_categories: pii,
      retention_days: retention,
    });
  }

  return (
    <Card>
      <form
        onSubmit={handleSubmit}
        data-testid="add-node-form"
        style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '0.875rem' }}>
          Add node
        </div>
        {error && <Alert variant="error">{error}</Alert>}
        <Field id="node-id" label="ID">
          <Input
            value={form.id}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((f) => ({ ...f, id: e.target.value }))
            }
            placeholder="e.g. training_data"
          />
        </Field>
        <Field id="node-name" label="Name">
          <Input
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((f) => ({ ...f, name: e.target.value }))
            }
            placeholder="Training dataset"
          />
        </Field>
        <Field id="node-type" label="Type">
          <Select
            value={form.type}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setForm((f) => ({ ...f, type: e.target.value as NodeType }))
            }
          >
            <option value="dataset">Dataset</option>
            <option value="transform">Transform</option>
            <option value="model">Model</option>
          </Select>
        </Field>
        <Field id="node-pii" label="PII categories (comma-separated)">
          <Input
            value={form.pii_categories}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((f) => ({ ...f, pii_categories: e.target.value }))
            }
            placeholder="email, name, ssn"
          />
        </Field>
        <Field id="node-retention" label="Retention (days)">
          <Input
            type="number"
            value={form.retention_days}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((f) => ({ ...f, retention_days: e.target.value }))
            }
            placeholder="365"
          />
        </Field>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="submit">Add</Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ============================================================
// Add edge form
// ============================================================

function AddEdgeForm({
  nodes,
  existingEdges,
  onAdd,
  onCancel,
}: {
  nodes: LineageNode[];
  existingEdges: LineageEdge[];
  onAdd: (edge: LineageEdge) => void;
  onCancel: () => void;
}) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!from || !to) { setError('Select both source and target.'); return; }
    if (from === to) { setError('Source and target must differ.'); return; }
    if (existingEdges.some((ed) => ed.from === from && ed.to === to)) {
      setError('This edge already exists.');
      return;
    }
    onAdd({ from, to });
  }

  return (
    <Card>
      <form
        onSubmit={handleSubmit}
        data-testid="add-edge-form"
        style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
      >
        <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '0.875rem' }}>
          Add edge
        </div>
        {error && <Alert variant="error">{error}</Alert>}
        <Field id="edge-source" label="Source">
          <Select value={from} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFrom(e.target.value)}>
            <option value="">Select source</option>
            {nodes.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name} ({n.type})
              </option>
            ))}
          </Select>
        </Field>
        <Field id="edge-target" label="Target">
          <Select value={to} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTo(e.target.value)}>
            <option value="">Select target</option>
            {nodes.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name} ({n.type})
              </option>
            ))}
          </Select>
        </Field>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button type="submit">Add</Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ============================================================
// Main editor component
// ============================================================

export function DataLineageEditor({
  systemId,
  workspaceSlug,
  readOnly = false,
}: DataLineageEditorProps) {
  // Data fetch
  const lineageQuery = useQuery({
    queryKey: queryKeys.lineage(workspaceSlug, systemId),
    queryFn: () => fetchLineage(workspaceSlug, systemId),
    enabled: !!workspaceSlug && !!systemId,
  });
  const lineage = {
    data: lineageQuery.data ?? null,
    error: lineageQuery.error?.message ?? null,
    loading: lineageQuery.isLoading,
    refetch: lineageQuery.refetch,
  };

  // Local graph state (working copy for edits)
  const [localGraph, setLocalGraph] = useState<LineageGraph | null>(null);
  const [revision, setRevision] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Sync fetched data into local state
  useEffect(() => {
    if (lineage.data) {
      setLocalGraph(lineage.data.graph);
      setRevision(lineage.data.revision);
      setDirty(false);
    }
  }, [lineage.data]);

  // UI state
  const [showAddNode, setShowAddNode] = useState(false);
  const [showAddEdge, setShowAddEdge] = useState(false);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  // Pan + zoom
  const svgRef = useRef<SVGSVGElement>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const graph = localGraph ?? { nodes: [], edges: [] };
  const layout = useMemo(() => layoutGraph(graph.nodes, graph.edges), [graph]);

  // Compute SVG viewBox
  const positions = [...layout.values()];
  const PADDING = 80;
  const minX = positions.length ? Math.min(...positions.map((p) => p.x)) - PADDING : -200;
  const maxX = positions.length ? Math.max(...positions.map((p) => p.x)) + PADDING : 200;
  const minY = positions.length ? Math.min(...positions.map((p) => p.y)) - PADDING : -50;
  const maxY = positions.length ? Math.max(...positions.map((p) => p.y)) + PADDING : 200;
  const width = Math.max(maxX - minX, 400);
  const height = Math.max(maxY - minY, 250);

  // Existing IDs for validation
  const existingIds = useMemo(() => new Set(graph.nodes.map((n) => n.id)), [graph.nodes]);

  // ---- Handlers ----

  const handleAddNode = useCallback(
    (node: LineageNode) => {
      setLocalGraph((prev) => {
        const g = prev ?? { nodes: [], edges: [] };
        return { ...g, nodes: [...g.nodes, node] };
      });
      setDirty(true);
      setShowAddNode(false);
    },
    [],
  );

  const handleDeleteNode = useCallback(
    (id: string) => {
      setLocalGraph((prev) => {
        if (!prev) return prev;
        return {
          nodes: prev.nodes.filter((n) => n.id !== id),
          edges: prev.edges.filter((e) => e.from !== id && e.to !== id),
        };
      });
      setDirty(true);
      if (selectedNode === id) setSelectedNode(null);
    },
    [selectedNode],
  );

  const handleAddEdge = useCallback(
    (edge: LineageEdge) => {
      setLocalGraph((prev) => {
        const g = prev ?? { nodes: [], edges: [] };
        return { ...g, edges: [...g.edges, edge] };
      });
      setDirty(true);
      setShowAddEdge(false);
    },
    [],
  );

  const handleDeleteEdge = useCallback(
    (from: string, to: string) => {
      setLocalGraph((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          edges: prev.edges.filter((e) => !(e.from === from && e.to === to)),
        };
      });
      setDirty(true);
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!localGraph) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await saveLineage(workspaceSlug, systemId, localGraph, revision);
      setRevision(result.revision);
      setDirty(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [localGraph, revision, workspaceSlug, systemId]);

  // Pan handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (e.button !== 0) return;
      setDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    },
    [pan],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!dragging) return;
      setPan({
        x: dragStart.current.panX + (e.clientX - dragStart.current.x) / zoom,
        y: dragStart.current.panY + (e.clientY - dragStart.current.y) / zoom,
      });
    },
    [dragging, zoom],
  );

  const handleMouseUp = useCallback(() => setDragging(false), []);

  const handleWheel = useCallback((e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    setZoom((z) => Math.max(0.2, Math.min(3, z - e.deltaY * 0.001)));
  }, []);

  // ---- Render ----

  if (lineage.loading) return <Spinner />;
  if (lineage.error) return <Alert variant="error">{lineage.error}</Alert>;

  if (graph.nodes.length === 0 && !showAddNode) {
    return (
      <div data-testid="lineage-editor">
        <EmptyState
          title="No data lineage"
          description="Add nodes to map the data flow for this AI system."
        />
        {!readOnly && (
          <div style={{ marginTop: '12px' }}>
            <Button data-testid="add-node-btn" onClick={() => setShowAddNode(true)}>
              Add first node
            </Button>
          </div>
        )}
        {showAddNode && (
          <div style={{ marginTop: '12px', maxWidth: '400px' }}>
            <AddNodeForm
              onAdd={handleAddNode}
              onCancel={() => setShowAddNode(false)}
              existingIds={existingIds}
            />
          </div>
        )}
      </div>
    );
  }

  const selectedNodeData = graph.nodes.find((n) => n.id === selectedNode);

  return (
    <div data-testid="lineage-editor" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Toolbar */}
      {!readOnly && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Button data-testid="add-node-btn" onClick={() => { setShowAddNode(true); setShowAddEdge(false); }}>
            Add node
          </Button>
          <Button onClick={() => { setShowAddEdge(true); setShowAddNode(false); }}>
            Add edge
          </Button>
          {dirty && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>
      )}

      {saveError && <Alert variant="error">{saveError}</Alert>}

      {/* Forms */}
      {showAddNode && !readOnly && (
        <div style={{ maxWidth: '400px' }}>
          <AddNodeForm
            onAdd={handleAddNode}
            onCancel={() => setShowAddNode(false)}
            existingIds={existingIds}
          />
        </div>
      )}
      {showAddEdge && !readOnly && (
        <div style={{ maxWidth: '400px' }}>
          <AddEdgeForm
            nodes={graph.nodes}
            existingEdges={graph.edges}
            onAdd={handleAddEdge}
            onCancel={() => setShowAddEdge(false)}
          />
        </div>
      )}

      {/* SVG canvas */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: '6px',
          background: 'var(--surface)',
          overflow: 'hidden',
          cursor: dragging ? 'grabbing' : 'grab',
        }}
      >
        <svg
          ref={svgRef}
          viewBox={`${minX - pan.x / zoom} ${minY - pan.y / zoom} ${width / zoom} ${height / zoom}`}
          width="100%"
          height="400"
          style={{ display: 'block' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          data-testid="lineage-svg"
        >
          <ArrowMarker />

          {/* Edges */}
          {graph.edges.map((e) => (
            <EdgeLine key={`${e.from}-${e.to}`} from={e.from} to={e.to} layout={layout} />
          ))}

          {/* Nodes */}
          {graph.nodes.map((node) => {
            const pos = layout.get(node.id);
            if (!pos) return null;
            const isSelected = selectedNode === node.id;

            return (
              <g
                key={node.id}
                onClick={(ev) => {
                  ev.stopPropagation();
                  setSelectedNode(isSelected ? null : node.id);
                }}
                style={{ cursor: 'pointer' }}
                data-testid={`node-${node.id}`}
              >
                {/* Selection ring */}
                {isSelected && (
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={NODE_RADIUS + 4}
                    fill="none"
                    stroke="var(--accent)"
                    strokeWidth="2"
                    strokeDasharray="4 2"
                  />
                )}

                {/* Background circle */}
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={NODE_RADIUS}
                  fill="var(--surface)"
                  stroke="var(--border)"
                  strokeWidth="1"
                />

                {/* Type icon */}
                <NodeIcon type={node.type} x={pos.x} y={pos.y} />

                {/* PII count badge */}
                <PiiBadge count={node.pii_categories.length} x={pos.x} y={pos.y} />

                {/* Label */}
                <text
                  x={pos.x}
                  y={pos.y + NODE_RADIUS + 16}
                  textAnchor="middle"
                  fontSize="12"
                  fill="var(--text)"
                  fontWeight="500"
                >
                  {node.name}
                </text>

                {/* Type sublabel */}
                <text
                  x={pos.x}
                  y={pos.y + NODE_RADIUS + 30}
                  textAnchor="middle"
                  fontSize="10"
                  fill="var(--text-dim)"
                >
                  {node.type}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Selected node detail panel */}
      {selectedNodeData && (
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                {selectedNodeData.name}
              </span>
              <span
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-dim)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                {selectedNodeData.id}
              </span>
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              Type: {selectedNodeData.type}
            </div>
            {selectedNodeData.pii_categories.length > 0 && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                PII: {selectedNodeData.pii_categories.join(', ')}
              </div>
            )}
            {selectedNodeData.retention_days !== null && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                Retention: {selectedNodeData.retention_days} days
              </div>
            )}

            {/* Edges from/to this node */}
            {graph.edges.filter((e) => e.from === selectedNodeData.id || e.to === selectedNodeData.id).length > 0 && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-dim)', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
                <strong>Connections:</strong>
                {graph.edges
                  .filter((e) => e.from === selectedNodeData.id || e.to === selectedNodeData.id)
                  .map((e) => {
                    const other = e.from === selectedNodeData.id ? e.to : e.from;
                    const dir = e.from === selectedNodeData.id ? '->' : '<-';
                    return (
                      <div key={`${e.from}-${e.to}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                        <span>{dir} {other}</span>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => handleDeleteEdge(e.from, e.to)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--danger-fg)',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              padding: 0,
                            }}
                          >
                            remove
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {!readOnly && (
              <button
                type="button"
                onClick={() => handleDeleteNode(selectedNodeData.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--danger-fg)',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '0.8125rem',
                  textAlign: 'left',
                  marginTop: '4px',
                }}
              >
                Delete node
              </button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
